// components/MultiLocationMap.jsx
import { useMemo, useEffect, memo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix para los iconos de Leaflet en React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Componente para crear iconos personalizados con etiquetas
const createCustomIcon = (label, color = '#dc2626') => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 36px;
        height: 36px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 4px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <span style="
          transform: rotate(45deg);
          color: white;
          font-weight: bold;
          font-size: 14px;
        ">${label}</span>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36],
  });
};

// Componente para ajustar el mapa automáticamente a todos los marcadores
const AutoFitBounds = ({ locations }) => {
  const map = useMap();

  useEffect(() => {
    if (locations && locations.length > 0) {
      const validLocations = locations.filter(loc => loc.lat && loc.lng);
      
      if (validLocations.length === 0) return;

      if (validLocations.length === 1) {
        // Si solo hay una ubicación, centrar en ella
        map.setView([validLocations[0].lat, validLocations[0].lng], 16);
      } else {
        // Si hay múltiples ubicaciones, ajustar para mostrar todas
        const bounds = L.latLngBounds(
          validLocations.map(loc => [loc.lat, loc.lng])
        );
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [locations, map]);

  return null;
};

const MultiLocationMap = ({ locations }) => {
  // Coordenadas por defecto (Ecuador - centro aproximado)
  const DEFAULT_CENTER = {
    lat: -1.8312,
    lng: -78.6156
  };

  // Procesar ubicaciones y validar coordenadas
  const mappedLocations = useMemo(() => {
    if (!locations || locations.length === 0) {
      return [];
    }

    const validLocations = [];

    locations.forEach((location) => {
      // Validar que las coordenadas existan y sean números válidos
      const lat = parseFloat(location.lat);
      const lng = parseFloat(location.lng);

      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        // Coordenadas válidas
        validLocations.push({
          ...location,
          lat,
          lng,
        });
      }
    });

    return validLocations;
  }, [locations]);

  // Calcular el centro dinámico basado en todas las ubicaciones
  const mapCenter = useMemo(() => {
    if (mappedLocations.length === 0) {
      return DEFAULT_CENTER;
    }

    if (mappedLocations.length === 1) {
      return {
        lat: mappedLocations[0].lat,
        lng: mappedLocations[0].lng,
      };
    }

    // Calcular el centro promedio de todas las ubicaciones
    const avgLat = mappedLocations.reduce((sum, loc) => sum + loc.lat, 0) / mappedLocations.length;
    const avgLng = mappedLocations.reduce((sum, loc) => sum + loc.lng, 0) / mappedLocations.length;

    return { lat: avgLat, lng: avgLng };
  }, [mappedLocations]);

  // Calcular el zoom inicial basado en la dispersión de ubicaciones
  const initialZoom = useMemo(() => {
    if (mappedLocations.length === 0) return 13;
    if (mappedLocations.length === 1) return 16;

    // Calcular la distancia máxima entre ubicaciones
    let maxDistance = 0;
    for (let i = 0; i < mappedLocations.length; i++) {
      for (let j = i + 1; j < mappedLocations.length; j++) {
        const distance = Math.sqrt(
          Math.pow(mappedLocations[i].lat - mappedLocations[j].lat, 2) +
          Math.pow(mappedLocations[i].lng - mappedLocations[j].lng, 2)
        );
        maxDistance = Math.max(maxDistance, distance);
      }
    }

    // Ajustar zoom basado en la distancia
    if (maxDistance < 0.01) return 15;
    if (maxDistance < 0.05) return 13;
    if (maxDistance < 0.1) return 12;
    if (maxDistance < 0.5) return 10;
    return 8;
  }, [mappedLocations]);

  // Si no hay ubicaciones válidas
  if (mappedLocations.length === 0) {
    return (
      <div className="w-full h-[500px] rounded-2xl bg-gray-100 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md">
            <svg className="w-12 h-12 text-yellow-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-yellow-800 font-semibold mb-2">Ubicaciones no disponibles</p>
            <p className="text-yellow-600 text-sm">
              Los establecimientos aún no tienen coordenadas configuradas.
              Por favor, agrega las coordenadas en el panel de administración.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-lg relative">
      <MapContainer
        center={[mapCenter.lat, mapCenter.lng]}
        zoom={initialZoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Renderizar marcadores */}
        {mappedLocations.map((location, index) => (
          <Marker
            key={`${location.name}-${index}`}
            position={[location.lat, location.lng]}
            icon={createCustomIcon(location.label)}
          >
            <Popup>
              <div className="p-2 min-w-[200px]">
                <h3 className="font-bold text-red-600 mb-2 text-base">{location.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{location.address}</p>
                <div className="text-xs text-gray-500 border-t pt-2 mt-2">
                  <p>📍 Lat: {location.lat.toFixed(6)}</p>
                  <p>📍 Lng: {location.lng.toFixed(6)}</p>
                </div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-3 bg-red-600 text-white text-center py-2 px-3 rounded text-sm hover:bg-red-700 transition-colors"
                >
                  Abrir en Google Maps
                </a>
              </div>
            </Popup>
          </Marker>
        ))}
        
        {/* Ajustar automáticamente el mapa */}
        <AutoFitBounds locations={mappedLocations} />
      </MapContainer>

      {/* Contador de ubicaciones */}
      <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg px-3 py-2 z-[1000]">
        <p className="text-sm font-semibold text-gray-700">
          📍 {mappedLocations.length} {mappedLocations.length === 1 ? 'Ubicación' : 'Ubicaciones'}
        </p>
      </div>
    </div>
  );
};

// Memorizar el componente para evitar re-renders innecesarios
export default memo(MultiLocationMap, (prevProps, nextProps) => {
  // Solo re-renderizar si las ubicaciones cambian
  return JSON.stringify(prevProps.locations) === JSON.stringify(nextProps.locations);
});
